import { Button, ButtonSizeTypes, ButtonType, ModalTypes, StandardModal, Table, TableColumn, TableMetaProps } from '@digicert/dcone-common-ui'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { Translate } from 'react-localize-redux'
import EditUserSideRail from '../EditUserSideRail'
import { UserUpdateType } from '../user-overview/UserOverview.types'
import styles from './AccessCredentials.module.scss'
import { connect } from 'react-redux'
import { IApiToken } from 'reducers/Reducers.interfaces'
import { formatDate, translate } from 'shared/helpers/utils'
import { LIMIT_10 } from 'core/utils/constants'
import { DISABLED, EXPIRED, VALID } from 'core/constants'
import moment from 'moment'
import { deleteApiToken, editApiToken, fetchApiTokens } from 'secure-pages/api-tokens/ApiTokens.actions'
import { statusPillRenderer } from 'core/utils/renders.helpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil } from '@fortawesome/pro-light-svg-icons'
import { Icon } from '@fortawesome/fontawesome-svg-core'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

const ApiTokenList = ({ user, profile, fetchApiTokens, deleteApiToken, editApiToken }): ReactElement => {

  const defaultMeta: TableMetaProps<IApiToken> = {
    pageSize: LIMIT_10,
    currentPage: 1,
    totalItems: 0,
    filters: {},
    sort: {
      columnKey: "name",
      order: "ascend"
    }
  }

  const [meta,] = useState(defaultMeta)
  const [tokens, setTokens] = React.useState<any>([])
  const [tokenId, setTokenId] = useState('')
  const [token, setToken] = useState<IApiToken>()
  const [showSideRail, setShowSideRail] = React.useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [sideRailContent, setSideRailContent] = useState(UserUpdateType.ApiToken)
  const [loading, setLoading] = useState(true)


  const getTableColumns = (): TableColumn<IApiToken>[] => [
    {
      key: 'name',
      title: translate('common.form.name') as string,
      sorter: false,
      displayRowActions: true,
      render: apiToken => (
        <>
          {apiToken.name}
        </>

      )
    },
    {
      key: 'status',
      title: translate('common.status') as string,
      sorter: false,
      render: apiToken => (
        <>{moment.utc(apiToken.end_date).diff(moment.utc()) < 0 ? statusPillRenderer(EXPIRED) : apiToken.enabled ? statusPillRenderer(VALID) : statusPillRenderer(DISABLED)}</>
      )
    },
    {
      key: 'start_date',
      title: translate('common.form.validFrom') as string,
      sorter: false,
      render: apiToken => (
        <>{formatDate(apiToken.start_date)}</>
      )

    },
    {
      key: 'end_date',
      title: translate('common.form.validTo') as string,
      sorter: false,
      render: apiToken => (
        <>{apiToken.end_date ? formatDate(apiToken.end_date) : '-'}</>
      )

    },
    {
      key: 'masked_api_key',
      title: translate('serviceUser.tokenID') as string,
      sorter: false,
      render: apiToken => (
        <>{apiToken.masked_api_key}</>
      )
    }
  ]

  const tableChange = useCallback(
    () => {
      console.log('Waiting on backend to sort')
    },
    []
  )

  useEffect(() => {
    setLoading(false)
    if (profile.id === user.id) {
      fetchApiTokens().then((response: any) => {
        setTokens(response.items)
      })
    }
  }, [])

  const onEditAction = (item: IApiToken): void => {
    setToken(item)
    setShowSideRail(true)
    setSideRailContent(UserUpdateType.UpdateApiToken)
  }

  const onApiTokenDelete = (id: string) => {
    setTokenId(id)
    setIsDeleteModalVisible(true)
  }

  const onEnableDisableToken = (item: IApiToken): void => {
    if (item) {
      const payload = {
        name: item.name,
        end_date: item.end_date,
        permissions: item.permissions,
        enabled: !item.enabled
      }
      editApiToken(item.id, payload).then(() => {
        //@ts-ignore
        fetchApiTokens().then((response: any) => {
          setTokens(response.items)
        })
      })
    }
  }

  const getRowActions = (item) => {
    return [
      {
        title: translate('apiTokens.editToken') as string,
        isVisible: true,
        onClick: () => onEditAction(item),
        icon: <FontAwesomeIcon icon={faPencil as Icon as IconProp}/>
      },
      {
        title: translate('apiTokens.deleteToken') as string,
        isVisible: true,
        onClick: () => onApiTokenDelete(item.id)
      },
      {
        title: !item.enabled ? translate('clientAuth.enable') as string : translate('clientAuth.disable') as string,
        isVisible: true,
        onClick: () => onEnableDisableToken(item)
      }
    ]
  }

  const deleteToken = (): void => {
    deleteApiToken([tokenId]).then(() => {
      fetchApiTokens().then((response: any) => {
        setTokens(response.items)
      })
    })
    setIsDeleteModalVisible(false)
  }

  return (
    <section className={styles.accessCredentials}>
      <header className={styles.header}>
        <div>
          <h3><Translate id='navigation.apiTokens' /></h3>
        </div>
        {profile.id === user.id &&
          <Button size={ButtonSizeTypes.SMALL} buttonType={ButtonType.SECONDARY} onClick={() => {
            setSideRailContent(UserUpdateType.ApiToken)
            setShowSideRail(true)
          }}>
            <Translate id='apiTokens.createBtn' />
          </Button>
        }
      </header>
        <Table
          className={styles.table + ' ' + 'resizable-table-body'}
          columns={getTableColumns()}
          items={tokens}
          getRowKey={api => api.id}
          getActions={(item) => getRowActions(item)}
          actionsButtonsQuantity={1}
          meta={meta}
          onChange={tableChange}
          disableCheckBoxes
          loading={loading}
        />
      <EditUserSideRail
        user={user}
        showSideRail={showSideRail}
        sideRailContent={sideRailContent}
        setShowSideRail={setShowSideRail}
        setTokens={setTokens}
        token={token}
        profile={profile}
      />

      {isDeleteModalVisible && (
        <StandardModal
          modalType={ModalTypes.DESTRUCTIVE}
          headline={translate('apiTokens.delete.title') as string}
          buttonLabel={translate('apiTokens.delete.buttonLabel') as string}
          buttonCancelLabel={translate('common.form.buttons.cancelBtn') as string}
          display={isDeleteModalVisible}
          onOkCallBack={deleteToken}
          onCancelCallBack={() => {
            setIsDeleteModalVisible(false)
          }}
        >
          <Translate id='apiTokens.delete.content' />
        </StandardModal>
      )}
    </section>
  )
}
export default connect(null, { fetchApiTokens, deleteApiToken, editApiToken })(ApiTokenList);
